import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { ROLE_COLORS } from "./UserCard";

const colors = [
  "#3D9AD1", // Light Blue
  "#9CBCE0", // Sky Blue
  "#FFC20E", // Vivid Yellow
  "#FF9F06", // Golden Orange
  "#FF8200", // Vibrant Orange
];

const MetricsHeader = ({ data }) => {
  const cumulativeTime =
    (data.timeDistribution?.reduce((a, b) => a + b, 0) || 0) / 3600;
  const {
    totalUsers,
    activeNow,
    activeRecently,
    timeDistribution,
    roleDistribution,
  } = data;

  const activeUsersRef = useRef(null);
  const sessionTimeRef = useRef(null);
  const roleDistributionRef = useRef(null);
  const joinDateRef = useRef(null);

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      renderUserChart();
      renderTimeChart();
      renderRoleChart();
      renderOnboardingChart();
    }
  }, [data]);

  const renderUserChart = () => {
    const width = 185;
    const height = 100;
    const radius = Math.min(width, height) / 2;

    d3.select(activeUsersRef.current).selectAll("*").remove();

    const svg = d3
      .select(activeUsersRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const arc = d3
      .arc()
      .innerRadius(radius * 0.7)
      .outerRadius(radius);

    const pie = d3
      .pie()
      .value((d) => d.value)
      .sort(null);

    const data = [
      { name: "Active Now", value: activeNow },
      { name: "Recently Active", value: activeRecently },
      { name: "Inactive", value: totalUsers - activeNow - activeRecently },
    ];

    const colorScale = d3
      .scaleOrdinal()
      .domain(["Active Now", "Recently Active", "Inactive"])
      .range(["#22c55e", "#eab308", "#4b5563"]);

    const group = svg
      .selectAll(".arc")
      .data(pie(data))
      .enter()
      .append("g")
      .attr("class", "arc");

    group
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => colorScale(d.data.name))
      .attr("stroke", "#1a202c")
      .attr("stroke-width", 0);

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.30em")
      .attr("class", "active-users")
      .attr("fill", "#9CBCE0")
      .attr("font-size", "28px")
      .attr("font-face", "Roboto")
      .text(activeNow);

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dx", "4.7em")
      .attr("dy", "4.0em")
      .attr("class", "total-users")
      .attr("fill", "#9CBCE0")
      .attr("font-size", "11px")
      .text(`/ ${totalUsers}`);

    // outline the donut
    // svg
    //   .append("circle")
    //   .attr("cx", 0)
    //   .attr("cy", 0)
    //   .attr("r", radius * 0.7)
    //   .attr("fill", "none")
    //   .attr("stroke", "#fff")
    //   .attr("stroke-width", 0.1);

    // svg
    //   .append("circle")
    //   .attr("cx", 0)
    //   .attr("cy", 0)
    //   .attr("r", radius)
    //   .attr("fill", "none")
    //   .attr("stroke", "#fff")
    //   .attr("stroke-width", 0.1);

    // Add tooltip
    const tooltip = d3
      .select(activeUsersRef.current)
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      //.style("background-color", "rgba(0, 0, 0, 0.7)")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("font-size", "9px")
      .style("pointer-events", "none");

    group
      .on("mouseover", (event, d) => {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip
          .html(`${d.data.value} ${d.data.name.toLowerCase()}`)
          .style("left", event.pageX + "px")
          .style("top", event.pageY - 28 + "px");
      })
      .on("mouseout", () => {
        tooltip.transition().duration(500).style("opacity", 0);
      });
  };

  const renderTimeChart = () => {
    const width = 185;
    const height = 150;
    const margin = { top: 0, right: 0, bottom: 0, left: 0 };

    d3.select(sessionTimeRef.current).selectAll("*").remove();

    const svg = d3
      .select(sessionTimeRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    if (!timeDistribution || timeDistribution.length === 0) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("fill", "#9ca3af")
        .attr("font-size", "9px")
        .text("No data available");
      return;
    }

    const timeDistributionHours = timeDistribution.map(
      (seconds) => seconds / 3600
    );
    const averageTimeSpent = d3.mean(timeDistributionHours);
    const maxTime = d3.max(timeDistributionHours);

    const x = d3
      .scaleLinear()
      .domain([0, timeDistributionHours.length - 1])
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([0, maxTime])
      .range([height - margin.bottom, margin.top]);

    // Create area
    const area = d3
      .area()
      .x((d, i) => x(i))
      .y0(height - margin.bottom)
      .y1((d) => y(d))
      .curve(d3.curveMonotoneX);

    // Create line
    const line = d3
      .line()
      .x((d, i) => x(i))
      .y((d) => y(d))
      .curve(d3.curveMonotoneX);

    // Add area
    svg
      .append("path")
      .datum(timeDistributionHours)
      .attr("fill", "rgba(255, 0, 255, 0.1)")
      .attr("d", area);

    // Add line
    svg
      .append("path")
      .datum(timeDistributionHours)
      .attr("fill", "none")
      .attr("stroke", "fuchsia")
      .attr("stroke-width", 0.5)
      .attr("d", line);

    // Add average line
    svg
      .append("line")
      .attr("x1", margin.left)
      .attr("x2", width - margin.right)
      .attr("y1", y(averageTimeSpent))
      .attr("y2", y(averageTimeSpent))
      .attr("stroke", colors[3])
      .attr("stroke-width", 0.5)
      .attr("stroke-dasharray", "3");

    svg
      .append("text")
      .attr("x", width - margin.right)
      .attr("y", y(averageTimeSpent) - 5)
      .attr("text-anchor", "end")
      .attr("fill", colors[3])
      .attr("font-size", "9px")
      .text("Avg " + averageTimeSpent.toFixed(2) + "h");

    // Add tooltip
    const tooltip = d3
      .select(sessionTimeRef.current)
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("font-size", "9px")
      .style("pointer-events", "none");

    svg
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .style("fill", "none")
      .style("pointer-events", "all")
      .on("mousemove", (event) => {
        const [xPos] = d3.pointer(event);
        const xValue = Math.round(x.invert(xPos));
        if (xValue >= 0 && xValue < timeDistributionHours.length) {
          const yValue = timeDistributionHours[xValue];
          tooltip.transition().duration(200).style("opacity", 0.9);
          tooltip
            .html(`${yValue.toFixed(2)} hours`)
            .style("left", event.pageX + "px")
            .style("top", event.pageY - 28 + "px");
        }
      })
      .on("mouseout", () => {
        tooltip.transition().duration(500).style("opacity", 0);
      });
  };

  const renderRoleChart = () => {
    const width = 185;
    const height = 150;
    const margin = { top: 10, right: 10, bottom: 10, left: 80 };
    const maxBarHeight = 16;

    d3.select(roleDistributionRef.current).selectAll("*").remove();
    const svg = d3
      .select(roleDistributionRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    if (!roleDistribution || Object.keys(roleDistribution).length === 0) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("fill", "#9ca3af")
        .attr("font-size", "9px")
        .text("No data available");
      return;
    }

    // Sort roles by count, descending
    const sortedRoles = Object.entries(roleDistribution).sort(
      (a, b) => b[1] - a[1]
    );

    // Calculate total height needed for bars
    //const totalBarHeight = sortedRoles.length * maxBarHeight;
    const availableHeight = height - margin.top - margin.bottom;

    // Determine actual bar height and spacing
    const barHeight = Math.min(
      maxBarHeight,
      availableHeight / sortedRoles.length
    );
    const barSpacing =
      (availableHeight - barHeight * sortedRoles.length) /
      (sortedRoles.length + 1);

    // Calculate start Y position to center the bars
    const startY =
      margin.top +
      (availableHeight -
        (barHeight * sortedRoles.length +
          barSpacing * (sortedRoles.length - 1))) /
        2;

    // const y = d3
    //   .scaleBand()
    //   .domain(sortedRoles.map((d) => d[0]))
    //   .range([
    //     startY,
    //     startY +
    //       barHeight * sortedRoles.length +
    //       barSpacing * (sortedRoles.length - 1),
    //   ])
    //   .padding(0.1);

    const x = d3
      .scaleLinear()
      .domain([0, d3.max(sortedRoles, (d) => d[1])])
      .range([margin.left, width - margin.right]);

    // Create bars
    svg
      .selectAll("rect")
      .data(sortedRoles)
      .enter()
      .append("rect")
      .attr("y", (d, i) => startY + i * (barHeight + barSpacing))
      .attr("x", margin.left)
      .attr("height", barHeight)
      .attr("width", (d) => x(d[1]) - margin.left)
      .attr("rx", 5)
      .attr("stroke", (d) => ROLE_COLORS[d[0]] || "gray")
      .attr("fill", (d) => `${ROLE_COLORS[d[0]] || "gray"}1a`);

    // Add role labels
    svg
      .selectAll(".role-label")
      .data(sortedRoles)
      .enter()
      .append("text")
      .attr("class", "role-label")
      .attr(
        "y",
        (d, i) => startY + i * (barHeight + barSpacing) + barHeight / 2
      )
      .attr("x", margin.left - 5)
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#9ca3af")
      .attr("font-size", "9px")
      .text((d) => d[0]);

    // Add hover effect and tooltip
    const tooltip = d3
      .select(roleDistributionRef.current)
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("font-size", "9px")
      .style("pointer-events", "none");

    svg
      .selectAll("rect")
      .on("mouseover", (event, d) => {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip
          .html(`${d[1]}`)
          .style("left", event.pageX + "px")
          .style("top", event.pageY - 28 + "px");
      })
      .on("mouseout", () => {
        tooltip.transition().duration(500).style("opacity", 0);
      });
  };

  const renderOnboardingChart = () => {
    const width = 185;
    const height = 150;
    const margin = { top: 10, right: 10, bottom: 20, left: 10 };

    d3.select(joinDateRef.current).selectAll("*").remove();

    const svg = d3
      .select(joinDateRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    if (!data.joinDates || data.joinDates.length === 0) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("fill", "#9ca3af")
        .attr("font-size", "9px")
        .text("No data available");
      return;
    }

    // Convert dates to local midnight
    const joinDates = data.joinDates.map((d) => {
      const date = new Date(d);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    });

    const now = new Date();
    const oneWeekAgo = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 7
    );

    // Group dates by day
    const dateGroups = d3.group(joinDates, (d) => d.getTime());
    const sortedDates = Array.from(dateGroups).sort((a, b) => a[0] - b[0]);

    // Create bins from the grouped data
    const bins = sortedDates.map(([timestamp, dates]) => ({
      x0: new Date(timestamp),
      x1: new Date(timestamp + 86400000), // Add one day in milliseconds
      length: dates.length,
    }));

    const x = d3
      .scaleTime()
      .domain([bins[0].x0, bins[bins.length - 1].x1])
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(bins, (d) => d.length)])
      .range([height - margin.bottom, margin.top]);

    // Create tooltip
    const tooltip = d3
      .select(joinDateRef.current)
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("font-size", "9px")
      .style("pointer-events", "none");

    // Create bars
    svg
      .selectAll("rect")
      .data(bins)
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.x0))
      .attr("width", (d) => Math.max(0, x(d.x1) - x(d.x0) - 1))
      .attr("y", (d) => y(d.length))
      .attr("height", (d) => height - margin.bottom - y(d.length))
      .attr("fill", (d) =>
        d.x0 >= oneWeekAgo ? ROLE_COLORS["champion"] : "rgba(255, 0, 255, 0.5)"
      )
      .attr("rx", 3)
      .attr("ry", 3)
      .on("mouseover", (event, d) => {
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip
          .html(
            `${d.length} users joined<br>${d3.timeFormat("%b %d, %Y")(d.x0)}`
          )
          .style("left", event.pageX + "px")
          .style("top", event.pageY - 28 + "px");
      })
      .on("mouseout", () => {
        tooltip.transition().duration(500).style("opacity", 0);
      });

    // Add x-axis (without labels)
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(""))
      .attr("color", ROLE_COLORS["champion"]);

    // Calculate recent joins
    const recentJoins = joinDates.filter((d) => d >= oneWeekAgo).length;

    // Add recent joins text
    svg
      .append("text")
      .attr("x", width - margin.right)
      .attr("y", margin.top)
      .attr("text-anchor", "end")
      .attr("fill", colors[3])
      .attr("font-size", "9px")
      .text(`Last 7 days: ${recentJoins}`);
  };

  return (
    <div className="ml-4 rounded-lg w-48 mr-4 overflow-hidden">
      <div className="space-y-16">
        <div>
          <h3 className="text-sm font-semibold text-gray-400 text-center mb-2 mt-4 lg:mt-0">
            Active Users
          </h3>
          <div ref={activeUsersRef}></div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-400 text-center">
            Time Investment
          </h3>
          <div ref={sessionTimeRef}></div>
          {Object.keys(roleDistribution || {}).length > 0 && (
            <p className="text-xs text-gray-400 mt-1 text-center">
              {cumulativeTime.toFixed(0)} Hours by All Users
            </p>
          )}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-400 text-center">
            Role Engagement
          </h3>
          <div ref={roleDistributionRef}></div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-400 text-center mb-2">
            Onboarding Pattern
          </h3>
          <div ref={joinDateRef}></div>
        </div>
      </div>
    </div>
  );
};

export default MetricsHeader;
